import { InvalidVizConfig, VizConfig } from "@/shared/config";
import { ConfigService } from "@/shared/domain/config";
import {
  LayoutDefinition,
  LayoutRecord,
  LayoutService,
  InvalidLayoutDefinitionError,
  UnsupportedLayoutSchemaVersion,
  createEmptyLayout,
} from "@/shared/domain/layouts";
import { layoutQueryKeys } from "@/shared/domain/layouts/hooks";
import {
  InvalidWorkspaceConfigV2Error,
  WorkspaceConfigV2,
  WorkspaceService,
  isWorkspaceConfigV2,
  parseWorkspaceConfigV2,
} from "@/shared/domain/workspaces";
import { RobotoNotFoundException } from "@/shared/http";
import {
  RobotoLayoutConflictException,
  RobotoUnauthorizedException,
} from "@/shared/http/exceptions";
import { ErrorMonitoringService } from "@/shared/services";
import { LoggerService } from "@/shared/services/LoggerService";
import {
  type WorkspaceSettingsDefaults,
  parseWorkspaceSettingsDefaults,
} from "@/shared/state/visualization";
import { nanoSecToSecStr } from "@/shared/time";

import {
  InvalidWorkspaceConfigError,
  WorkspaceNotFoundError,
} from "./exceptions";
import {
  LAYOUT_CONFLICT_ERROR_NAME,
  type ConfigSource,
  type VisualizerInitContext,
  type VisualizerNotice,
} from "./visualizerInit";

/**
 * Resolve the visualizer's config source from a shared workspace link.
 *
 * @throws UnrecoverableError when the workspace is missing or its config is invalid
 * @throws Error for unexpected failures
 */
export async function resolveByWorkspaceId(
  ctx: VisualizerInitContext,
): Promise<ConfigSource> {
  const { workspaceId, orgId } = ctx;

  LoggerService.log("Attempting to resolve by workspace id", workspaceId);

  if (!workspaceId) {
    return null;
  }

  const workspaceService = new WorkspaceService(ctx.httpClient);

  const getWorkspaceQuery = () => {
    return ctx.queryClient.ensureQueryData({
      queryKey: ["workspace", workspaceId],
      queryFn: ({ signal }) =>
        workspaceService.getWorkspace(orgId, workspaceId, {
          abortSignal: signal,
        }),
    });
  };

  try {
    const workspace = await getWorkspaceQuery();
    // v1's `version` is optional, so the v2 check must come first.
    if (isWorkspaceConfigV2(workspace.config)) {
      return await resolveWorkspaceV2(ctx, workspaceId, workspace.config);
    }
    const vizConfig = VizConfig.from_obj(workspace.config);
    return { type: "workspace", value: vizConfig.toObject() };
  } catch (error) {
    // Expected errors
    if (error instanceof RobotoUnauthorizedException) {
      throw error;
    }
    if (error instanceof RobotoNotFoundException) {
      throw new WorkspaceNotFoundError(workspaceId, { cause: error });
    }
    if (
      error instanceof InvalidVizConfig ||
      error instanceof InvalidWorkspaceConfigV2Error ||
      error instanceof InvalidLayoutDefinitionError ||
      error instanceof UnsupportedLayoutSchemaVersion
    ) {
      throw new InvalidWorkspaceConfigError(workspaceId, { cause: error });
    }

    // Unexpected error
    throw new Error(`Failed to resolve workspace ${workspaceId}`, {
      cause: error,
    });
  }
}

/**
 * Resolve a v2 workspace config: its layout (fetched by id, or wrapped from
 * the inline definition) plus the datasources and playhead it carries.
 *
 * @throws InvalidWorkspaceConfigV2Error | InvalidLayoutDefinitionError |
 *   UnsupportedLayoutSchemaVersion when the config or its inline layout is
 *   malformed; the caller maps these to InvalidWorkspaceConfigError
 */
async function resolveWorkspaceV2(
  ctx: VisualizerInitContext,
  workspaceId: string,
  rawConfig: unknown,
): Promise<ConfigSource> {
  const config = parseWorkspaceConfigV2(rawConfig);
  const notices: VisualizerNotice[] = [];

  const layout = await resolveWorkspaceV2Layout(ctx, config, notices);

  LoggerService.log("Resolved v2 workspace", workspaceId);

  return {
    type: "layout",
    origin: "workspace-v2",
    value: layout,
    datasources: config.datasources,
    timestamp: config.t !== undefined ? nanoSecToSecStr(config.t) : undefined,
    notices,
  };
}

/**
 * The layout a v2 workspace references. A saved layout that is gone or that
 * this user may not view degrades to an empty layout plus an error notice
 * rather than failing the whole open: the workspace's data is what the link
 * is for, and the layout only arranges it.
 *
 * @throws InvalidLayoutDefinitionError | UnsupportedLayoutSchemaVersion when
 *   the inline definition is malformed
 */
async function resolveWorkspaceV2Layout(
  ctx: VisualizerInitContext,
  config: WorkspaceConfigV2,
  notices: VisualizerNotice[],
): Promise<LayoutRecord> {
  const { layoutId, layoutDefinition, schemaVersion } = config.layout;

  if (layoutId === undefined) {
    return temporaryLayoutRecordFrom({ layoutDefinition, schemaVersion });
  }

  try {
    return await fetchLayoutById(ctx, layoutId);
  } catch (error) {
    if (error instanceof RobotoNotFoundException) {
      notices.push({
        name: "WorkspaceLayoutNotFoundNotice",
        severity: "error",
        message: `Layout '${layoutId}' referenced by this workspace was not found; opened with its data only.`,
      });
      return createEmptyLayout();
    }
    if (error instanceof RobotoUnauthorizedException) {
      notices.push({
        name: "WorkspaceLayoutUnauthorizedNotice",
        severity: "error",
        message: `You don't have access to layout '${layoutId}' referenced by this workspace; opened with its data only.`,
      });
      return createEmptyLayout();
    }
    throw error;
  }
}

/**
 * Fetch a saved layout by id through the query cache, so the route's
 * components find it already resolved under `layoutQueryKeys.id`.
 */
function fetchLayoutById(
  ctx: VisualizerInitContext,
  layoutId: string,
): Promise<LayoutRecord> {
  const layoutService = new LayoutService(ctx.httpClient);
  return ctx.queryClient.ensureQueryData({
    queryKey: layoutQueryKeys.id(layoutId),
    queryFn: ({ signal }) =>
      layoutService.getLayoutById(layoutId, ctx.orgId, {
        abortSignal: signal,
      }),
  });
}

export async function resolveByLayoutId(
  ctx: VisualizerInitContext,
): Promise<ConfigSource> {
  const { layoutId } = ctx;

  LoggerService.log("Attempting to resolve by layout id", layoutId);

  if (!layoutId) {
    return null;
  }

  try {
    const layout = await fetchLayoutById(ctx, layoutId);
    return { type: "layout", value: layout, origin: "layout-id" };
  } catch (error) {
    // Expected errors
    if (error instanceof RobotoUnauthorizedException) {
      throw error;
    }
    if (error instanceof RobotoNotFoundException) {
      return {
        type: "recoverable-error",
        value: {
          name: "LayoutNotFoundError",
          message: `Layout '${layoutId}' not found.`,
        },
      };
    }

    // Unexpected error
    throw new Error(`Failed to resolve layout ${layoutId}`, {
      cause: error,
    });
  }
}

export async function resolveByLayoutName(
  ctx: VisualizerInitContext,
): Promise<ConfigSource> {
  const { layoutName, orgId } = ctx;

  LoggerService.log("Attempting to resolve by layout name", layoutName);

  if (!layoutName) {
    return null;
  }

  const layoutService = new LayoutService(ctx.httpClient);

  const getLayoutByNameQuery = () => {
    return ctx.queryClient.ensureQueryData({
      queryKey: layoutQueryKeys.name(layoutName),
      queryFn: ({ signal }) =>
        layoutService.getLayoutByName(layoutName, orgId, {
          abortSignal: signal,
        }),
    });
  };

  try {
    const layout = await getLayoutByNameQuery();
    return { type: "layout", value: layout, origin: "layout-name" };
  } catch (error) {
    // Expected errors
    if (error instanceof RobotoUnauthorizedException) {
      throw error;
    }
    if (error instanceof RobotoNotFoundException) {
      return {
        type: "recoverable-error",
        value: {
          name: "LayoutNotFoundError",
          message: `Layout '${layoutName}' not found.`,
        },
      };
    }

    if (error instanceof RobotoLayoutConflictException) {
      return {
        type: "recoverable-error",
        value: {
          name: LAYOUT_CONFLICT_ERROR_NAME,
          title: "Ambiguous layout",
          message: `Found multiple layouts named '${layoutName}'`,
          metadata: { conflictingLayouts: error.conflictingLayouts },
        },
      };
    }

    // Unexpected error
    throw new Error(`Failed to resolve layout ${layoutName}`, {
      cause: error,
    });
  }
}

// Not `async` like its siblings: the body never awaits, and the lint rule
// against await-less async functions applies.
export function resolveByLayoutFromNavigationState(
  ctx: VisualizerInitContext,
): Promise<ConfigSource> {
  const { layoutFromNavState } = ctx;

  LoggerService.log(
    "Attempting to resolve by layout definition from navigation state",
    layoutFromNavState,
  );

  if (!layoutFromNavState) {
    return Promise.resolve(null);
  }

  try {
    return Promise.resolve({
      type: "layout",
      value: temporaryLayoutRecordFrom(layoutFromNavState),
      origin: "nav-state",
    });
  } catch (error) {
    if (
      error instanceof InvalidLayoutDefinitionError ||
      error instanceof UnsupportedLayoutSchemaVersion
    ) {
      // A caller handed off a malformed layout — a programming error.
      return Promise.reject(error);
    }

    // Unexpected error
    return Promise.reject(
      new Error("Failed to resolve layout definition from navigation state", {
        cause: error,
      }),
    );
  }
}

/**
 * Wrap a transported layout definition in a temporary `LayoutRecord`.
 * `createEmptyLayout()`'s spread keeps `TEMPORARY_LAYOUT_ID`, which is what
 * tells the workspace this layout is unsaved (e.g. stale `?layout=` params
 * are stripped rather than synced).
 *
 * @throws InvalidLayoutDefinitionError if the definition is not valid
 * @throws UnsupportedLayoutSchemaVersion if the schema version is unsupported
 */
export function temporaryLayoutRecordFrom(navState: {
  layoutDefinition: unknown;
  schemaVersion: number;
}): LayoutRecord {
  const layoutDefinition = LayoutDefinition.fromObj(
    navState.layoutDefinition,
    navState.schemaVersion,
  );

  return {
    ...createEmptyLayout(),
    layout_definition: layoutDefinition.state,
    schema_version: layoutDefinition.schemaVersion,
  };
}

export async function resolveByDefaultLayout(
  ctx: VisualizerInitContext,
): Promise<ConfigSource> {
  LoggerService.log("Attempting to resolve by default layout ID for user");

  const configService = new ConfigService(ctx.httpClient);

  try {
    // Fetched straight from the service rather than pre-warmed into the query
    // cache: `useUserConfig` partitions its cache entries by user id, and this
    // runs in a react-router loader with no access to auth state, so there is
    // no key here that the route's components would later read from.
    const userDefaultLayoutId = await configService.getUserConfig<string>(
      "user_default_layout_id",
    );

    if (!userDefaultLayoutId) {
      return null;
    }

    try {
      const layout = await fetchLayoutById(ctx, userDefaultLayoutId);
      return { type: "layout", value: layout, origin: "default" };
    } catch (error) {
      // Expected errors
      if (error instanceof RobotoNotFoundException) {
        // User's default layout was deleted. Clear the config (non-blocking).
        void configService.setUserConfig("user_default_layout_id", "");

        // Then return a recoverable error
        return {
          type: "recoverable-error",
          value: {
            name: "DefaultLayoutNotFoundError",
            message: "Default layout not found",
          },
        };
      }

      if (error instanceof RobotoUnauthorizedException) {
        return {
          type: "recoverable-error",
          value: {
            name: "UnauthorizedError",
            message: "Unable to access default layout.",
          },
        };
      }

      throw error;
    }
  } catch (error) {
    // Expected errors
    if (error instanceof RobotoUnauthorizedException) {
      throw error;
    }

    // Unexpected error
    throw new Error("Failed to get user's default layout from config", {
      cause: error,
    });
  }
}

/**
 * The viewer's `workspace_settings_defaults`, which seed a fresh session. The
 * loader never calls this when restoring a workspace, whose recorded config
 * governs. Never rejects — any failure resolves to `undefined`, leaving the
 * session on system defaults — so the loader may leave it pending. Read
 * straight from the service for the same reason as the default layout above.
 */
export async function fetchWorkspaceSettingsDefaults(
  ctx: VisualizerInitContext,
): Promise<WorkspaceSettingsDefaults | undefined> {
  try {
    const configService = new ConfigService(ctx.httpClient);
    const value = await configService.getUserConfig<unknown>(
      "workspace_settings_defaults",
    );
    // An unset key comes back null. Kept as `undefined` rather than parsed
    // into `{}` so "no defaults" has one representation on the wire: the
    // loader's `json()` drops an undefined field but keeps an empty object.
    return value === null ? undefined : parseWorkspaceSettingsDefaults(value);
  } catch (error) {
    ErrorMonitoringService.captureError(error);
    return undefined;
  }
}
